// Push Notification Service for Web

// Check if running in Capacitor native app
let isCapacitorNative = false;
try {
  const { Capacitor } = require('@capacitor/core');
  isCapacitorNative = Capacitor.isNativePlatform();
} catch (e) {
  // Capacitor not available
}

// Get API URL - handle Capacitor native app
const getApiUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // If native Capacitor app
  if (isCapacitorNative) {
    return 'https://bizilgo.com';
  }
  
  // For Capacitor web or protocol detection
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
      return 'https://bizilgo.com';
    }
    if (protocol === 'https:' || protocol === 'http:') {
      return window.location.origin;
    }
  }
  
  return 'https://bizilgo.com';
};

const API = getApiUrl();

// Audio context for playing sounds
let audioContext = null;
let whistleBuffer = null;

// Initialize audio context and load whistle sound
async function initAudio() {
  if (audioContext) return;
  
  try {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Load whistle sound
    const response = await fetch('/whistle.wav');
    const arrayBuffer = await response.arrayBuffer();
    whistleBuffer = await audioContext.decodeAudioData(arrayBuffer);
  } catch (error) {
    console.error('Failed to initialize audio:', error);
  }
}

// Play whistle sound
export async function playWhistleSound() {
  try {
    // Initialize on first play (requires user interaction)
    if (!audioContext) {
      await initAudio();
    }
    
    if (!audioContext || !whistleBuffer) {
      // Fallback to Audio element
      const audio = new Audio('/whistle.wav');
      audio.volume = 0.7;
      await audio.play();
      return;
    }
    
    // Resume context if suspended
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    
    // Create buffer source and play
    const source = audioContext.createBufferSource();
    source.buffer = whistleBuffer;
    
    // Add some gain
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.7;
    
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    source.start(0);
  } catch (error) {
    console.error('Failed to play whistle sound:', error);
    // Final fallback
    try {
      const audio = new Audio('/whistle.wav');
      await audio.play();
    } catch (e) {
      console.warn('Audio fallback play failed:', e.message);
    }
  }
}

// Setup service worker message listener for playing sounds
export function setupNotificationSoundHandler() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', async (event) => {
      if (event.data?.type === 'PLAY_WHISTLE_SOUND') {
        await playWhistleSound();
      }
    });
  }
}

// Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 
         'PushManager' in window &&
         'Notification' in window;
}

// Get current permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

// Subscribe to push notifications
export async function subscribeToPush(userId, userType) {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return null;
  }

  try {
    // Get VAPID public key from server
    const response = await fetch(`${API}/api/push/vapid-public-key`);
    const { publicKey } = await response.json();

    if (!publicKey) {
      console.error('No VAPID public key available');
      return null;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    // Send subscription to server
    const subscriptionJson = subscription.toJSON();
    await fetch(`${API}/api/push/subscribe/web`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        user_type: userType,
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
        device_type: 'web'
      })
    });

    // Push subscription successful
    return subscription;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return null;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(userId) {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      
      // Remove from server
      await fetch(`${API}/api/push/unsubscribe/${userId}?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE'
      });
    }

    return true;
  } catch (error) {
    console.error('Unsubscribe failed:', error);
    return false;
  }
}

// Check if user is subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
}

// Show a local notification (for testing or app-triggered notifications)
export function showLocalNotification(title, body, options = {}) {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    console.warn('Cannot show notification - permission not granted');
    return;
  }

  return new Notification(title, {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    ...options
  });
}
